/**
 * As rotas do Feedback — spec 0108.
 *
 * O envio de arquivo é em três passos (RN-05): o servidor grava a linha e
 * devolve a URL assinada, o navegador envia direto ao Storage, e a
 * confirmação confere o tamanho no bucket. Os bytes nunca passam pelo servidor.
 */
import { authFetch } from '../../services/api';

export type Tipo = 'PROBLEMA' | 'SUGESTAO' | 'DUVIDA' | 'ELOGIO';
export type Status = 'RECEBIDO' | 'EM_ANALISE' | 'RESPONDIDO' | 'RESOLVIDO';
export type Canal = 'PAINEL' | 'PORTAL' | 'APP';

export const TIPOS: Array<{ valor: Tipo; rotulo: string; dica: string }> = [
  { valor: 'PROBLEMA', rotulo: 'Problema', dica: 'O que você estava fazendo e o que aconteceu de errado?' },
  { valor: 'SUGESTAO', rotulo: 'Sugestão', dica: 'O que mudaria no seu trabalho?' },
  { valor: 'DUVIDA', rotulo: 'Dúvida', dica: 'Em que tela você está e o que quer saber?' },
  { valor: 'ELOGIO', rotulo: 'Elogio', dica: 'O que funcionou bem?' },
];
export const ROTULO_TIPO: Record<Tipo, string> = { PROBLEMA: 'Problema', SUGESTAO: 'Sugestão', DUVIDA: 'Dúvida', ELOGIO: 'Elogio' };
export const ROTULO_STATUS: Record<Status, string> = {
  RECEBIDO: 'Recebido', EM_ANALISE: 'Em análise', RESPONDIDO: 'Respondido', RESOLVIDO: 'Resolvido',
};
export const PAPEL_STATUS: Record<Status, 'atencao' | 'info' | 'sucesso' | 'neutro'> = {
  RECEBIDO: 'atencao', EM_ANALISE: 'info', RESPONDIDO: 'sucesso', RESOLVIDO: 'neutro',
};
export const ROTULO_CANAL: Record<Canal, string> = { PAINEL: 'Painel', PORTAL: 'Portal', APP: 'App' };

/** O mesmo do servidor (FR-004): a recusa acontece antes do envio. */
export const MB = 1024 * 1024;
export const TETO_BYTES = 50 * MB;
export const MAX_ANEXOS = 5;
export const EXTENSOES_ACEITAS = [
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'pdf', 'mp4', 'mov',
  'xls', 'xlsx', 'csv', 'ods', 'doc', 'docx', 'odt', 'txt',
];
export const ACEITA_NO_SELETOR = EXTENSOES_ACEITAS.map((e) => `.${e}`).join(',');

export function motivoDaRecusa(arquivo: File): string | null {
  const ext = arquivo.name.includes('.') ? arquivo.name.split('.').pop()!.toLowerCase() : '';
  if (!EXTENSOES_ACEITAS.includes(ext)) return 'Tipo não aceito';
  if (arquivo.size <= 0) return 'Arquivo vazio';
  if (arquivo.size > TETO_BYTES) return 'Acima de 50 MB';
  return null;
}

export function tamanhoLegivel(bytes: number): string {
  if (bytes < MB) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / MB).toFixed(1).replace('.', ',')} MB`;
}

export interface ArquivoVisto {
  idArquivo: number;
  idMensagem: number | null;
  papel: 'CAPTURA' | 'ANEXO';
  nome: string;
  tamanho: number;
  situacao: 'PENDENTE' | 'ENVIADO' | 'EXPURGADO';
}

export interface MensagemVista {
  idMensagem: number;
  autorTipo: 'PESSOA' | 'EQUIPE' | 'NOTA' | 'EVENTO';
  autor: string;
  texto: string;
  criadoEm: string;
}

export interface FeedbackResumo {
  id: number;
  tipo: Tipo;
  status: Status;
  canal: Canal;
  mensagem: string;
  tela: string | null;
  criadoEm: string;
  atualizadoEm: string;
  naoLidas?: number;
  nomeAutor?: string;
  perfil?: string | null;
  assumidoPor?: string | null;
  arquivos?: number;
}

export interface FeedbackDetalhe extends Omit<FeedbackResumo, 'arquivos' | 'nomeAutor' | 'assumidoPor'> {
  endereco: string | null;
  perfil: string | null;
  resolvidoEm: string | null;
  arquivosExpurgadosEm: string | null;
  mensagens: MensagemVista[];
  arquivos: ArquivoVisto[];
  nomeAutor?: string | null;
  assumidoPor?: string | null;
}

interface Envio { idArquivo: number; nome: string; papel: 'CAPTURA' | 'ANEXO'; urlEnvio: string }

async function lerJson<T>(r: Response, padrao: string): Promise<T> {
  if (!r.ok) {
    const corpo = await r.json().catch(() => ({}));
    throw new Error(corpo?.error || padrao);
  }
  return r.json() as Promise<T>;
}

const postar = (url: string, corpo: unknown) => authFetch(url, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corpo ?? {}),
});

/** O arquivo vai ao Storage pela URL assinada, sem o JWT do painel (o mesmo formato da Ajuda). */
async function enviarAoStorage(url: string, arquivo: Blob, nome: string): Promise<boolean> {
  const corpo = new FormData();
  corpo.append('cacheControl', '3600');
  corpo.append('', arquivo, nome);
  try {
    const r = await fetch(url, { method: 'PUT', headers: { 'x-upsert': 'false' }, body: corpo });
    return r.ok;
  } catch {
    return false;
  }
}

/**
 * Envia os arquivos pedidos e confirma. Devolve quantos não chegaram: o
 * relato já está gravado, e a tela diz o que faltou em vez de desfazer tudo.
 */
async function enviarArquivos(
  envios: Envio[], arquivos: Array<{ nome: string; dados: Blob }>, confirmar: string,
): Promise<number> {
  // Sem URL nenhuma, o servidor gravou o texto e não conseguiu preparar os arquivos.
  if (envios.length === 0) return arquivos.length;
  const enviados: number[] = [];
  const usados = new Set<number>();
  for (const e of envios) {
    const i = arquivos.findIndex((a, k) => !usados.has(k) && a.nome === e.nome);
    if (i < 0) continue;
    usados.add(i);
    if (await enviarAoStorage(e.urlEnvio, arquivos[i].dados, e.nome)) enviados.push(e.idArquivo);
  }
  const r = await lerJson<{ confirmados: number[]; faltando: number[] }>(
    await postar(confirmar, { ids: enviados }), 'Não foi possível conferir os arquivos.',
  );
  return arquivos.length - r.confirmados.length;
}

export interface Rascunho {
  tipo: Tipo;
  mensagem: string;
  tela: string;
  endereco: string;
  captura: Blob | null;
  anexos: File[];
}

export async function enviarFeedback(r: Rascunho): Promise<{ id: number; faltaram: number }> {
  const arquivos = [
    ...(r.captura ? [{ papel: 'CAPTURA' as const, nome: 'captura.jpg', dados: r.captura }] : []),
    ...r.anexos.map((a) => ({ papel: 'ANEXO' as const, nome: a.name, dados: a as Blob })),
  ];
  const resp = await lerJson<{ feedback: { id: number }; envios: Envio[] }>(
    await postar('/api/feedback', {
      tipo: r.tipo, mensagem: r.mensagem, tela: r.tela, endereco: r.endereco,
      arquivos: arquivos.map((a) => ({ papel: a.papel, nome: a.nome, tamanho: a.dados.size })),
    }),
    'Não foi possível enviar o feedback.',
  );
  const faltaram = await enviarArquivos(resp.envios, arquivos, `/api/feedback/${resp.feedback.id}/arquivos/confirmar`);
  return { id: resp.feedback.id, faltaram };
}

export async function lerMeus(): Promise<{ itens: FeedbackResumo[]; naoLidas: number }> {
  return lerJson(await authFetch('/api/feedback/meus'), 'Não foi possível ler os seus feedbacks.');
}

export async function lerResumo(): Promise<number> {
  const r = await lerJson<{ naoLidas: number }>(await authFetch('/api/feedback/meus/resumo'), '');
  return r.naoLidas;
}

export async function abrirMeu(id: number): Promise<FeedbackDetalhe> {
  return lerJson(await authFetch(`/api/feedback/meus/${id}`), 'Não foi possível abrir o feedback.');
}

export async function responderMeu(id: number, texto: string, anexos: File[]): Promise<number> {
  const resp = await lerJson<{ envios: Envio[] }>(
    await postar(`/api/feedback/meus/${id}/mensagens`, {
      texto, arquivos: anexos.map((a) => ({ papel: 'ANEXO', nome: a.name, tamanho: a.size })),
    }),
    'Não foi possível enviar a mensagem.',
  );
  return enviarArquivos(resp.envios, anexos.map((a) => ({ nome: a.name, dados: a })), `/api/feedback/${id}/arquivos/confirmar`);
}

export async function urlDoArquivo(idArquivo: number): Promise<string> {
  const r = await lerJson<{ url: string }>(await authFetch(`/api/feedback/arquivos/${idArquivo}/url`), 'Não foi possível abrir o arquivo.');
  return r.url;
}

// ── Quem atende ───────────────────────────────────────────────────────────

export interface Fila {
  itens: FeedbackResumo[];
  contagens: { porStatus: Record<Status, number>; porTipo: Record<Tipo, number>; total: number };
}

export async function lerFila(): Promise<Fila> {
  return lerJson(await authFetch('/api/feedback/fila'), 'Não foi possível ler a fila.');
}

export async function lerContadorDaFila(): Promise<number> {
  const r = await lerJson<{ recebidos: number }>(await authFetch('/api/feedback/fila/contador'), '');
  return r.recebidos;
}

export async function abrirNaFila(id: number): Promise<FeedbackDetalhe> {
  return lerJson(await postar(`/api/feedback/fila/${id}/abrir`, {}), 'Não foi possível abrir o feedback.');
}

export async function responderNaFila(id: number, texto: string, nota: boolean, anexos: File[]): Promise<number> {
  const resp = await lerJson<{ envios: Envio[] }>(
    await postar(`/api/feedback/fila/${id}/mensagens`, {
      texto, nota, arquivos: anexos.map((a) => ({ papel: 'ANEXO', nome: a.name, tamanho: a.size })),
    }),
    nota ? 'Não foi possível gravar a nota.' : 'Não foi possível enviar a resposta.',
  );
  return enviarArquivos(resp.envios, anexos.map((a) => ({ nome: a.name, dados: a })), `/api/feedback/fila/${id}/arquivos/confirmar`);
}

export async function assumir(id: number): Promise<void> {
  await lerJson(await postar(`/api/feedback/fila/${id}/assumir`, {}), 'Não foi possível assumir.');
}

export async function trocarTipo(id: number, tipo: Tipo): Promise<void> {
  await lerJson(await postar(`/api/feedback/fila/${id}/tipo`, { tipo }), 'Não foi possível trocar o tipo.');
}

export async function resolver(id: number, nota?: string): Promise<void> {
  await lerJson(await postar(`/api/feedback/fila/${id}/resolver`, { nota: nota?.trim() || undefined }), 'Não foi possível marcar como resolvido.');
}
